import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Landmark, Save, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance`;

const Field = ({ id, label, value, onChange, placeholder, help }) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-wider text-slate-600">{label}</Label>
    <Input id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
           placeholder={placeholder} data-testid={id} />
    {help && <p className="text-[10px] text-slate-500 mt-1">{help}</p>}
  </div>
);

const AdminBankingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const [meta, setMeta]       = useState({ updated_at: null, updated_by: null });
  const [form, setForm] = useState({
    beneficiary: '', beneficiary_address: '',
    ach_routing: '', ach_account: '', ach_type: 'Checking',
    wire_bank_name: '', wire_bank_address: '', wire_swift: '',
    cheque_payable_to: '', cheque_mail_to: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/banking-settings`);
      const raw = r.data?.raw || {};
      const eff = r.data?.effective || {};
      setForm({
        beneficiary: raw.beneficiary ?? eff.beneficiary ?? '',
        beneficiary_address: raw.beneficiary_address ?? eff.beneficiary_address ?? '',
        ach_routing: raw.ach_routing ?? eff.ach?.routing ?? '',
        ach_account: raw.ach_account ?? eff.ach?.account ?? '',
        ach_type:    raw.ach_type    ?? eff.ach?.type    ?? 'Checking',
        wire_bank_name:    raw.wire_bank_name    ?? eff.wire?.bank_name    ?? '',
        wire_bank_address: raw.wire_bank_address ?? eff.wire?.bank_address ?? '',
        wire_swift:        raw.wire_swift        ?? eff.wire?.swift        ?? '',
        cheque_payable_to: raw.cheque_payable_to ?? eff.cheque?.payable_to ?? '',
        cheque_mail_to:    raw.cheque_mail_to    ?? eff.cheque?.mail_to    ?? '',
      });
      setMeta({ updated_at: raw.updated_at || null, updated_by: raw.updated_by || null });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load banking settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/banking-settings`, form);
      toast({ title: 'Banking settings saved', description: 'Payment Instructions app-wide will reflect these values.' });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (err) return (
    <Card className="border-red-200 bg-red-50" data-testid="banking-settings-error">
      <CardContent className="p-6 text-sm text-red-800 flex gap-3">
        <ShieldAlert className="h-5 w-5 mt-0.5" />
        <div><p className="font-medium">Access denied</p><p>{err}</p></div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-3xl space-y-6" data-testid="banking-settings-page">
      <div className="flex items-start gap-3">
        <Landmark className="h-6 w-6 text-blue-700 mt-0.5" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Banking Settings</h1>
          <p className="text-sm text-slate-500 max-w-xl mt-1">
            Master banking details used app-wide by <b>Payment Instructions</b>, signed treasury links, and internal receipts.
            Restricted to Super Admin.
          </p>
          {meta.updated_at && (
            <p className="text-[11px] text-slate-400 mt-1">
              Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || '—'}
            </p>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Beneficiary</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Field id="beneficiary" label="Beneficiary" value={form.beneficiary}
                 onChange={(v) => setForm({ ...form, beneficiary: v })} placeholder="USKE Supply LLC" />
          <Field id="beneficiary_address" label="Beneficiary Address" value={form.beneficiary_address}
                 onChange={(v) => setForm({ ...form, beneficiary_address: v })} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">ACH</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Field id="ach_routing" label="Routing Number" value={form.ach_routing}
                 onChange={(v) => setForm({ ...form, ach_routing: v })} placeholder="9 digits" />
          <Field id="ach_account" label="Account Number" value={form.ach_account}
                 onChange={(v) => setForm({ ...form, ach_account: v })} />
          <Field id="ach_type" label="Account Type" value={form.ach_type}
                 onChange={(v) => setForm({ ...form, ach_type: v })} placeholder="Checking" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Wire</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Field id="wire_bank_name" label="Bank Name" value={form.wire_bank_name}
                 onChange={(v) => setForm({ ...form, wire_bank_name: v })} placeholder="KeyBank N.A." />
          <Field id="wire_bank_address" label="Bank Address" value={form.wire_bank_address}
                 onChange={(v) => setForm({ ...form, wire_bank_address: v })} />
          <Field id="wire_swift" label="SWIFT / BIC" value={form.wire_swift}
                 onChange={(v) => setForm({ ...form, wire_swift: v })} placeholder="KEYBUS33" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Cheque</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Field id="cheque_payable_to" label="Payable To" value={form.cheque_payable_to}
                 onChange={(v) => setForm({ ...form, cheque_payable_to: v })} />
          <Field id="cheque_mail_to" label="Mail To" value={form.cheque_mail_to}
                 onChange={(v) => setForm({ ...form, cheque_mail_to: v })} />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="banking-settings-save">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Save Banking Settings
        </Button>
      </div>
    </div>
  );
};

export default AdminBankingSettings;
